/**
 * Hook for tracking scroll position and detecting active Bible chapter.
 */

import { useEffect, useRef, useCallback } from 'react';
import { parseChapterId, formatChapterReference, formatAbbrevChapterReference, getChapterId } from '@skjv/common';
import { bibleState } from './state/bible-state';

interface ScrollTrackingResult {
  triggerChapterChange: (chapterId: string) => void;
  detectActiveChapter: () => void;
}

/**
 * Tracks scroll position, detects active chapter, and updates verse tracking.
 */
export function useScrollTracking(
  setDisplayedReference: (ref: string) => void,
  setAbbreviatedReference: (ref: string) => void,
  setIsChapterChanged: (changed: boolean) => void,
): ScrollTrackingResult {
  const lastTriggeredChapterRef = useRef<string | null>(null);
  const lastScrollYRef = useRef<number>(0);
  const scrollDirectionRef = useRef<'up' | 'down'>('down');
  const scrollIdleTimerRef = useRef<number | null>(null);
  const trackedVerseRef = useRef<number>(0);

  // Handle external navigation (bookmarks, history, etc.)
  useEffect(() => {
    if (bibleState.isScrollUpdate) return;

    const loc = bibleState.currentLocation;
    setDisplayedReference(formatChapterReference(loc));
    setAbbreviatedReference(formatAbbrevChapterReference(loc));
    lastTriggeredChapterRef.current = `chapter-${getChapterId(loc)}`;
    trackedVerseRef.current = loc.verseIndex;
  }, [
    bibleState.currentLocation.testamentIndex,
    bibleState.currentLocation.bookIndex,
    bibleState.currentLocation.chapterIndex,
    bibleState.currentLocation.verseIndex,
    bibleState.isScrollUpdate,
    setDisplayedReference,
    setAbbreviatedReference,
  ]);

  // Save verse position in state after scroll stops
  const updateDisplayWithVerse = useCallback(() => {
    const currentLoc = bibleState.currentLocation;
    const locationWithVerse = {
      ...currentLoc,
      verseIndex: trackedVerseRef.current,
    };
    bibleState.setLocationFromScroll(locationWithVerse);
  }, []);

  // Find verse visible below header during scroll
  const findVerseUnderHeader = useCallback(() => {
    const headerHeight = 140;
    const verses = document.querySelectorAll('.verse');

    for (const verse of verses) {
      const rect = verse.getBoundingClientRect();
      if (rect.top >= headerHeight - 20 && rect.top < window.innerHeight) {
        const verseId = verse.id;
        const parts = verseId.split('-');
        if (parts.length >= 5) {
          const verseIndex = parseInt(parts[4], 10);
          if (!isNaN(verseIndex)) {
            trackedVerseRef.current = verseIndex;
          }
        }
        break;
      }
    }
  }, []);

  // Track scroll direction and handle scroll-stop detection
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      scrollDirectionRef.current = currentScrollY > lastScrollYRef.current ? 'down' : 'up';
      lastScrollYRef.current = currentScrollY;
      findVerseUnderHeader();

      if (scrollIdleTimerRef.current) {
        window.clearTimeout(scrollIdleTimerRef.current);
      }
      scrollIdleTimerRef.current = window.setTimeout(() => {
        updateDisplayWithVerse();
      }, 1200);
    };

    const container = document.getElementById('bible-reader');
    const handleContainerScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const currentScrollY = target.scrollTop;
      scrollDirectionRef.current = currentScrollY > lastScrollYRef.current ? 'down' : 'up';
      lastScrollYRef.current = currentScrollY;
      findVerseUnderHeader();

      if (scrollIdleTimerRef.current) {
        window.clearTimeout(scrollIdleTimerRef.current);
      }
      scrollIdleTimerRef.current = window.setTimeout(() => {
        updateDisplayWithVerse();
      }, 1200);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    container?.addEventListener('scroll', handleContainerScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      container?.removeEventListener('scroll', handleContainerScroll);
      if (scrollIdleTimerRef.current) {
        window.clearTimeout(scrollIdleTimerRef.current);
      }
    };
  }, [findVerseUnderHeader, updateDisplayWithVerse]);

  // Trigger bounce and update location
  const triggerChapterChange = useCallback((chapterId: string) => {
    if (chapterId === lastTriggeredChapterRef.current) return;

    const idPart = chapterId.replace('chapter-', '');
    const location = parseChapterId(idPart);
    if (!location) return;

    lastTriggeredChapterRef.current = chapterId;
    bibleState.setLocationFromScroll(location);
    setDisplayedReference(formatChapterReference(location));
    setAbbreviatedReference(formatAbbrevChapterReference(location));
    if (scrollIdleTimerRef.current) {
      window.clearTimeout(scrollIdleTimerRef.current);
    }

    setIsChapterChanged(true);
    setTimeout(() => setIsChapterChanged(false), 600);
  }, [setDisplayedReference, setAbbreviatedReference, setIsChapterChanged]);

  // Detect active chapter based on which chapter's content is visible
  const detectActiveChapter = useCallback(() => {
    const READING_LINE = 150;
    const headings = document.querySelectorAll('.chapter-heading');
    let activeChapterId: string | null = null;
    let closestDistance = Infinity;

    for (const heading of headings) {
      const rect = heading.getBoundingClientRect();
      if (rect.top <= READING_LINE) {
        const distance = READING_LINE - rect.top;
        if (distance < closestDistance) {
          closestDistance = distance;
          const chapterEl = heading.closest('.chapter');
          activeChapterId = chapterEl?.id || null;
        }
      }
    }

    if (!activeChapterId) {
      const firstChapter = document.querySelector('.chapter');
      activeChapterId = firstChapter?.id || null;
    }

    if (activeChapterId) {
      triggerChapterChange(activeChapterId);
    }
  }, [triggerChapterChange]);

  // Set up scroll-based chapter detection
  useEffect(() => {
    let isTicking = false;
    const throttledDetection = () => {
      if (!isTicking) {
        window.requestAnimationFrame(() => {
          detectActiveChapter();
          isTicking = false;
        });
        isTicking = true;
      }
    };

    window.addEventListener('scroll', throttledDetection, { passive: true });
    const container = document.getElementById('bible-reader');
    container?.addEventListener('scroll', throttledDetection, { passive: true });
    detectActiveChapter();

    return () => {
      window.removeEventListener('scroll', throttledDetection);
      container?.removeEventListener('scroll', throttledDetection);
    };
  }, [detectActiveChapter]);

  return { triggerChapterChange, detectActiveChapter };
}
